/**
 * utils/transformations.js
 * THE CORE ENGINE. A single pure function applies a transformation atomically.
 *
 * Design rules (from BukuKas2_SpareParts_Plan.md §A4):
 *  - Recipes are editable templates, never fixed BOMs. The SAVED transformation
 *    (txn.inputs / txn.outputs) is the source of truth for stock — not any recipe.
 *  - Two workflows share one record type, differing only in line shape:
 *      ASSEMBLE  many inputs -> 1 output  (a SET can be built from finished parts too)
 *      TEARDOWN  1 input     -> many outputs
 *    (The apply logic is type-agnostic — it just consumes inputs and produces outputs.)
 *  - Lines reference items by internalCode only.
 *  - All-or-nothing: if any input/output item does not exist, the whole transformation
 *    is rejected and nothing in state changes. Insufficient stock is allowed — the
 *    resulting balance may go negative (borrowed/owed stock).
 *  - Phase-2 socket: lines may carry a future `cost` field; not used here.
 */
import { generateId } from "./idGenerators";

/** Map a bucket constant to the item stock field it touches. */
const bucketField = (bucket) => (bucket === "CABANG" ? "stokCabang" : "stokPusat");

/** Human label for the bucket, for error/history text. */
export const bucketLabel = (bucket) => (bucket === "CABANG" ? "Cabang" : "Pusat");

/**
 * Aggregate lines by internalCode so an item appearing twice is summed.
 * Skips blank codes and non-positive quantities. Returns Map<code, qty>.
 */
function aggregateLines(lines) {
  const m = new Map();
  for (const line of lines || []) {
    const code = (line.internalCode || "").trim();
    const qty = Number(line.qty) || 0;
    if (!code || qty <= 0) continue;
    m.set(code, (m.get(code) || 0) + qty);
  }
  return m;
}

/**
 * Apply a transformation to the data state.
 *
 * @param {object} state - { items, transformations, history, ... }
 * @param {object} txn   - { id?, type, inputs[], outputs[], bucket, date, notes, templateRef }
 * @returns {{ ok: boolean, error?: string, items?, transformations?, history?, transformation? }}
 *          On failure, returns { ok:false, error } and leaves state untouched (no partial application).
 *          On success, returns new items/transformations/history arrays plus the saved transformation.
 */
export function applyTransformation(state, txn) {
  const items = state.items || [];
  const field = bucketField(txn.bucket);

  const byCode = new Map(items.map((it) => [it.internalCode, it]));

  const inputAgg = aggregateLines(txn.inputs);
  const outputAgg = aggregateLines(txn.outputs);

  if (inputAgg.size === 0 && outputAgg.size === 0) {
    return { ok: false, error: "Transformasi kosong — tambahkan minimal satu baris input atau output." };
  }

  // ── 1. Validate every input/output item exists (missing → atomic reject) ────
  const missing = [];
  for (const [code] of inputAgg) {
    const it = byCode.get(code);
    if (!it) missing.push(code);
  }
  for (const [code] of outputAgg) {
    if (!byCode.has(code)) missing.push(code);
  }
  if (missing.length > 0) {
    return { ok: false, error: `Item tidak ditemukan: ${[...new Set(missing)].join(", ")}.` };
  }

  // ── 2 + 3. Apply deltas and record history (all changes staged, then committed) ─
  const id = txn.id || generateId();
  const when = txn.date || new Date().toISOString();
  const newHistory = [];
  const nextStock = new Map(); // code -> new bucket balance

  const applyDelta = (code, delta) => {
    const it = byCode.get(code);
    const before = nextStock.has(code) ? nextStock.get(code) : (Number(it[field]) || 0);
    const after = before + delta;
    nextStock.set(code, after);
    newHistory.push({
      id: generateId(),
      itemCode: code,
      date: when,
      kind: "TRANSFORMASI",
      bucket: txn.bucket,
      delta,
      balanceAfter: after,
      ref: id,
      // "Rakit (Pusat)" / "Urai (Cabang)" — the words the screens use (2026-10-09). Entries
      // written before this say "ASSEMBLE (Pusat)"; RiwayatList shows those as Rakit/Urai too.
      note: `${txn.type === "TEARDOWN" ? "Urai" : "Rakit"} (${bucketLabel(txn.bucket)})`,
    });
  };

  // Inputs consumed (−), then outputs produced (+).
  for (const [code, qty] of inputAgg) applyDelta(code, -qty);
  for (const [code, qty] of outputAgg) applyDelta(code, qty);

  const newItems = items.map((it) =>
    nextStock.has(it.internalCode)
      ? { ...it, [field]: nextStock.get(it.internalCode) }
      : it
  );

  // ── 4. Save the transformation record (saved lines = source of truth) ───────
  // substituteFor (Transformasi's sibling-substitution UI) lives on raw input lines, not the
  // aggregated map, so it's looked up back onto the rebuilt line — otherwise aggregateLines'
  // {internalCode, qty}-only reshape would silently drop it. Input-only; outputs never carry it.
  const substituteForByCode = new Map();
  for (const line of txn.inputs || []) {
    const code = (line.internalCode || "").trim();
    if (code && line.substituteFor && !substituteForByCode.has(code)) {
      substituteForByCode.set(code, line.substituteFor);
    }
  }
  const transformation = {
    id,
    type: txn.type,
    inputs: [...inputAgg].map(([internalCode, qty]) => {
      const substituteFor = substituteForByCode.get(internalCode);
      return substituteFor ? { internalCode, qty, substituteFor } : { internalCode, qty };
    }),
    outputs: [...outputAgg].map(([internalCode, qty]) => ({ internalCode, qty })),
    bucket: txn.bucket,
    date: when,
    notes: txn.notes || "",
    templateRef: txn.templateRef || null,
  };

  return {
    ok: true,
    items: newItems,
    transformations: [...(state.transformations || []), transformation],
    history: [...(state.history || []), ...newHistory],
    transformation,
  };
}
